import { Link } from 'react-router-dom';

const features = [
  {
    title: 'Automate Gate Entry & Tracking',
    desc: 'Automate and track vehicles entering with precise timestamps at every touchpoint through Vision AI, eliminating manual logging and congestion.',
  },
  {
    title: 'Dynamic Yard & Bay Direction',
    desc: 'Intelligently route vehicles to the exact next touchpoint—loading bay, unloading bay, or weighbridge—with predictive turnaround durations.',
  },
  {
    title: 'Seamless SAP Integration',
    desc: 'Direct integration with SAP and core business ERPs for seamless data synchronization across dispatch schedules, inventory, and gate passes.',
  },
  {
    title: 'Automated Dispatch & Duplicate Detection',
    desc: 'Vision AI-based automated dispatch inspection that instantly flags duplicate dispatches and mismatches to safeguard inventory accuracy.',
  },
  {
    title: 'Vision AI People Tracking',
    desc: 'Real-time worker movement analytics, direction of movement monitoring, and pose detection across operational zones on the shop floor.',
  },
  {
    title: 'PPE Kit Compliance',
    desc: 'Continuous automated visual auditing for mandatory safety gear (helmets, vests, goggles, and boots) to ensure total workplace safety compliance.',
  },
];

const ManufacturingPage = () => {
  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-white">
        <div className="absolute top-0 right-[-10%] w-[55vw] h-[55vw] bg-[#592aa7]/10 blur-[140px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-[-10%] w-[40vw] h-[40vw] bg-[#952877]/10 blur-[140px] rounded-full pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">

          {/* Back link */}
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-[#592aa7] transition-colors mb-10 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            All Products
          </Link>

          <h1 className="text-5xl md:text-7xl font-black text-[#17091a] leading-tight tracking-tighter mb-6">
            AI for{' '}
            <span className="bg-gradient-to-r from-[#592aa7] to-[#952877] bg-clip-text text-transparent">
              Manufacturing
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-500 max-w-2xl leading-relaxed font-light mb-10">
            Vision AI for gate automation, SAP-integrated yard routing, automated dispatch verification, and continuous workforce safety compliance.
          </p>

          <Link
            to="/contact"
            className="inline-block px-10 py-5 bg-[#17091a] text-white rounded-2xl font-bold text-base hover:bg-[#592aa7] transition-colors duration-300 shadow-xl hover:shadow-[#592aa7]/20 hover:scale-105 active:scale-95"
          >
            Start a Conversation
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="pb-28 px-6 mt-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#17091a] mb-4">
            What We{' '}
            <span className="bg-gradient-to-r from-[#592aa7] to-[#952877] bg-clip-text text-transparent">
              Deliver
            </span>
          </h2>
          <p className="text-gray-500 mb-14 text-lg max-w-xl">
            Vision AI architectures purpose-built for yard logistics, dispatch integrity, and factory safety.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((item, i) => (
              <div
                key={i}
                className="group p-8 bg-white rounded-[1.5rem] border border-gray-100 shadow-sm hover:shadow-[0_16px_40px_-12px_rgba(89,42,167,0.12)] transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#592aa7]/4 to-[#952877]/4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-[1.5rem]"></div>
                <div className="w-2 h-8 rounded-full bg-gradient-to-b from-[#592aa7] to-[#952877] mb-5"></div>
                <h3 className="text-lg font-bold text-[#17091a] mb-3 group-hover:text-[#592aa7] transition-colors relative z-10">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed relative z-10">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto bg-[#17091a] rounded-[2rem] p-12 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#592aa7]/20 to-[#952877]/20 rounded-[2rem]"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-3xl md:text-4xl font-black text-white mb-3">
                Ready to modernize your factory with AI?
              </h3>
              <p className="text-white/60 text-lg">We build solutions that work on your production floor.</p>
            </div>
            <Link
              to="/contact"
              className="flex-shrink-0 px-10 py-5 bg-white text-[#17091a] rounded-2xl font-bold text-base hover:bg-[#592aa7] hover:text-white transition-all duration-300 hover:scale-105 active:scale-95"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManufacturingPage;